'use client';

import * as React from 'react';
import { createPortal } from 'react-dom';
import { CircleAlert, CircleCheck, Info, TriangleAlert, X } from 'lucide-react';
import { cn } from './cn';
import { useUILabels } from './UIProvider';
import { IconButton } from './IconButton';
import { useMounted } from './hooks';

export type ToastVariant = 'default' | 'success' | 'error' | 'warning' | 'info';

export interface ToastOptions {
  title: React.ReactNode;
  description?: React.ReactNode;
  variant?: ToastVariant;
  /** ms; `0` keeps it until dismissed. Errors default to 8 s, everything else 4.5 s. */
  duration?: number;
  /** Single inline action ("ביטול", "פתיחה"). */
  action?: { label: string; onClick: () => void };
  /** Reuse an id to replace an existing toast (upload progress → upload done). */
  id?: string;
}

export interface ToastRecord extends ToastOptions {
  id: string;
  createdAt: number;
}

const MAX_VISIBLE = 4;

let toasts: ToastRecord[] = [];
const listeners = new Set<() => void>();
const timers = new Map<string, ReturnType<typeof setTimeout>>();
let counter = 0;

function emit() {
  toasts = [...toasts];
  for (const l of listeners) l();
}

function schedule(rec: ToastRecord) {
  const existing = timers.get(rec.id);
  if (existing) clearTimeout(existing);
  const fallback = rec.variant === 'error' ? 8000 : 4500;
  const duration = rec.duration ?? fallback;
  if (duration <= 0) return;
  timers.set(
    rec.id,
    setTimeout(() => dismissToast(rec.id), duration),
  );
}

/** Queue a toast from anywhere — event handlers, stores, fetch wrappers. */
export function toast(options: ToastOptions): string {
  const id = options.id ?? `toast-${++counter}`;
  const rec: ToastRecord = { variant: 'default', ...options, id, createdAt: Date.now() };
  const i = toasts.findIndex((t) => t.id === id);
  if (i >= 0) toasts[i] = rec;
  else toasts = [...toasts, rec].slice(-MAX_VISIBLE);
  schedule(rec);
  emit();
  return id;
}

export function dismissToast(id?: string) {
  if (id) {
    const t = timers.get(id);
    if (t) clearTimeout(t);
    timers.delete(id);
    toasts = toasts.filter((x) => x.id !== id);
  } else {
    for (const t of timers.values()) clearTimeout(t);
    timers.clear();
    toasts = [];
  }
  emit();
}

function subscribe(cb: () => void) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}
const getSnapshot = () => toasts;
const EMPTY_TOASTS: ToastRecord[] = [];
const getServerSnapshot = () => EMPTY_TOASTS;

export interface UseToastResult {
  toast: typeof toast;
  dismiss: typeof dismissToast;
  toasts: ToastRecord[];
}

/** `const { toast } = useToast()` inside components; `toast()` directly outside them. */
export function useToast(): UseToastResult {
  const list = React.useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  return { toast, dismiss: dismissToast, toasts: list };
}

const VARIANT_ICON: Record<ToastVariant, React.ReactNode> = {
  default: null,
  success: <CircleCheck className="size-5 text-success" />,
  error: <CircleAlert className="size-5 text-danger" />,
  warning: <TriangleAlert className="size-5 text-warning" />,
  info: <Info className="size-5 text-info" />,
};

export type ToasterPosition = 'bottom-center' | 'bottom-end' | 'top-center' | 'top-end';

const POSITION: Record<ToasterPosition, string> = {
  'bottom-center': 'bottom-0 start-0 end-0 items-center',
  'bottom-end': 'bottom-0 end-0 items-end',
  'top-center': 'top-0 start-0 end-0 items-center',
  'top-end': 'top-0 end-0 items-end',
};

export interface ToasterProps {
  /** Default: centred at the bottom on phones, anchored to the reading end on desktop. */
  position?: ToasterPosition;
  className?: string;
  /** Extra bottom padding so toasts clear the mobile tab bar. */
  offsetBottom?: string;
}

/** Mount once, near the root of the app. */
export function Toaster({ position, className, offsetBottom }: ToasterProps) {
  // The dismiss control speaks the app's language, not a hardcoded one (audit UX-42).
  const labels = useUILabels();
  const { toasts: list } = useToast();
  const mounted = useMounted();
  if (!mounted) return null;

  const pos = position ?? 'bottom-center';
  const isTop = pos.startsWith('top');

  return createPortal(
    <div
      role="region"
      aria-label="הודעות מערכת"
      className={cn(
        'pointer-events-none fixed z-(--z-toast) flex flex-col gap-2 p-4',
        'sm:max-w-sm',
        POSITION[pos],
        pos === 'bottom-center' && 'sm:start-auto sm:items-end',
        isTop ? 'pt-[max(1rem,env(safe-area-inset-top))]' : 'pb-[max(1rem,env(safe-area-inset-bottom))]',
        className,
      )}
      style={offsetBottom && !isTop ? { paddingBottom: offsetBottom } : undefined}
    >
      {list.map((t) => (
        <div
          key={t.id}
          role={t.variant === 'error' ? 'alert' : 'status'}
          aria-live={t.variant === 'error' ? 'assertive' : 'polite'}
          className={cn(
            'animate-toast-in pointer-events-auto flex w-full max-w-[26rem] items-start gap-3',
            'rounded-lg border border-line bg-surface p-3 shadow-lg',
          )}
        >
          {VARIANT_ICON[t.variant ?? 'default'] ? (
            <span className="mt-px shrink-0">{VARIANT_ICON[t.variant ?? 'default']}</span>
          ) : null}
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-ink">{t.title}</p>
            {t.description ? <p className="mt-0.5 text-xs text-ink-2">{t.description}</p> : null}
          </div>
          {t.action ? (
            <button
              type="button"
              onClick={() => {
                t.action?.onClick();
                dismissToast(t.id);
              }}
              className="k-hit shrink-0 rounded-sm px-2 py-1 text-sm font-medium text-accent hover:bg-accent-soft"
            >
              {t.action.label}
            </button>
          ) : null}
          <IconButton
            label={labels.close}
            size="sm"
            variant="ghost"
            className="-me-1 -mt-1 shrink-0"
            onClick={() => dismissToast(t.id)}
          >
            <X />
          </IconButton>
        </div>
      ))}
    </div>,
    document.body,
  );
}
